import React from "react";
import { createPortal } from "react-dom";
import {
  Alert, Box, Button, Chip, IconButton, MenuItem, Paper, Stack, TextField, Typography,
} from "@mui/material";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import QrCodeScannerRoundedIcon from "@mui/icons-material/QrCodeScannerRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import { ERP_BRAND, entityId, formatMoney, safeNumber } from "../../../utils/erp";
import { counterQuantity, counterSaleLineKey, isCounterScannerBurst } from "../../../utils/counterSale";

const emptyBurst = () => ({ text: "", firstAt: 0, lastAt: 0, maxGap: 0, key: "" });

export default function CounterSaleConsole({
  items, products, total, paid, pending, currency, paymentPanel, productSearch,
  searchInputRef, onLookupCode, onAddProduct, onUpdateItem, onSelectVariant,
  onRemoveItem, onBusyChange, onCheckout, saving, canConfirm, error, success,
  checkoutLocked = false,
  checkoutTarget,
}) {
  const [code, setCode] = React.useState("");
  const [queued, setQueued] = React.useState(0);
  const [activeKey, setActiveKey] = React.useState("");
  const [focusVersion, setFocusVersion] = React.useState(0);
  const [quantityEdit, setQuantityEdit] = React.useState(null);
  const [scanError, setScanError] = React.useState("");
  const [status, setStatus] = React.useState("Lector listo. Escaneá el primer ítem.");
  const [showSearch, setShowSearch] = React.useState(false);
  const codeRef = React.useRef(null);
  const quantityRefs = React.useRef(new Map());
  const burstRef = React.useRef(emptyBurst());
  const skipQuantityBlurRef = React.useRef(false);
  const queueRef = React.useRef(Promise.resolve());
  const pendingRef = React.useRef(0);
  const generationRef = React.useRef(0);
  const checkoutRef = React.useRef(null);
  const callbacksRef = React.useRef({ onLookupCode, onAddProduct });
  callbacksRef.current = { onLookupCode, onAddProduct };

  const focusCode = React.useCallback(() => {
    burstRef.current = emptyBurst();
    if (document.activeElement !== codeRef.current) {
      codeRef.current?.focus();
      codeRef.current?.select();
    }
  }, []);

  React.useEffect(() => {
    const timer = window.setTimeout(focusCode, 120);
    return () => {
      window.clearTimeout(timer);
      generationRef.current += 1;
    };
  }, [focusCode]);

  React.useEffect(() => {
    onBusyChange(queued > 0 || Boolean(code.trim()) || Boolean(quantityEdit && counterQuantity(quantityEdit.value) === null));
  }, [code, onBusyChange, quantityEdit, queued]);

  React.useEffect(() => {
    if (!activeKey || queued > 0 || saving || code) return undefined;
    const frame = window.requestAnimationFrame(() => {
      const input = quantityRefs.current.get(activeKey);
      input?.focus();
      input?.select();
      burstRef.current = emptyBurst();
    });
    return () => window.cancelAnimationFrame(frame);
  }, [activeKey, code, focusVersion, queued, saving]);

  const enqueueScan = (rawCode) => {
    const value = String(rawCode || "").trim();
    if (!value || saving || checkoutLocked) return;
    const generation = generationRef.current;
    setCode("");
    setQuantityEdit(null);
    setScanError("");
    pendingRef.current += 1;
    setQueued(pendingRef.current);
    // Keep accepting the next scan while lookups run, in scanner order.
    skipQuantityBlurRef.current = true;
    focusCode();
    skipQuantityBlurRef.current = false;
    queueRef.current = queueRef.current.then(async () => {
      if (generation !== generationRef.current) return;
      try {
        const { product, matchedVariantId } = await callbacksRef.current.onLookupCode(value);
        if (generation !== generationRef.current) return;
        const key = callbacksRef.current.onAddProduct(product, matchedVariantId);
        setActiveKey(key);
        setFocusVersion((current) => current + 1);
        setStatus(`${product.nombre || product.name || "Ítem"} agregado. Cambiá la cantidad o escaneá el próximo ítem.`);
      } catch (scanFailure) {
        if (generation !== generationRef.current) return;
        setScanError(scanFailure?.message || `No se pudo buscar el código ${value}.`);
        setActiveKey("");
        focusCode();
      } finally {
        if (generation === generationRef.current) {
          pendingRef.current -= 1;
          setQueued(pendingRef.current);
        }
      }
    });
  };

  const commitQuantity = () => {
    if (!quantityEdit) return true;
    const index = items.findIndex((item) => counterSaleLineKey(item) === quantityEdit.key);
    if (index < 0) { setQuantityEdit(null); return true; }
    const quantity = counterQuantity(quantityEdit.value);
    if (quantity === null) {
      setScanError("La cantidad debe ser un número mayor a cero.");
      return false;
    }
    onUpdateItem(index, { quantity: String(quantity) });
    setQuantityEdit(null);
    setScanError("");
    return true;
  };

  const handleKeys = (event) => {
    if (event.isComposing || event.nativeEvent?.isComposing || event.ctrlKey || event.metaKey || event.altKey) return;
    if (event.key === "F2") {
      event.preventDefault();
      if (!commitQuantity()) return;
      setActiveKey("");
      setShowSearch(true);
      window.requestAnimationFrame(() => searchInputRef.current?.focus());
      return;
    }
    if (event.key === "F4") {
      event.preventDefault();
      if (commitQuantity()) { setActiveKey(""); focusCode(); }
      return;
    }
    if (event.key === "F8") {
      event.preventDefault();
      if (pendingRef.current || code.trim() || !commitQuantity()) return;
      // Wait for the committed quantity and automatic payment quote to render.
      window.requestAnimationFrame(() => checkoutRef.current?.click());
      return;
    }
    const key = event.target?.dataset?.counterQuantity;
    if (!key) return;
    const now = event.timeStamp;
    if (event.key === "Enter") {
      event.preventDefault();
      event.stopPropagation();
      const burst = burstRef.current;
      burstRef.current = emptyBurst();
      if (isCounterScannerBurst(burst, now)) {
        // A reader may type while quantity is selected. Do not interpret its code as units.
        enqueueScan(burst.text);
      } else if (commitQuantity()) {
        setActiveKey("");
        setStatus("Cantidad confirmada. Escaneá el próximo ítem.");
        focusCode();
      }
      return;
    }
    if (event.key.length === 1 && !event.repeat) {
      const previous = burstRef.current;
      const gap = now - previous.lastAt;
      // Keep the full token even if rendering delays one key. Never look up a suffix.
      burstRef.current = previous.key === key
        ? { key, text: previous.text + event.key, firstAt: previous.firstAt, lastAt: now, maxGap: Math.max(previous.maxGap, gap) }
        : { key, text: event.key, firstAt: now, lastAt: now, maxGap: 0 };
    } else if (!["Shift", "Tab"].includes(event.key)) burstRef.current = emptyBurst();
  };

  const displayedItems = items.filter((item) => item.productId);
  const units = displayedItems.reduce((sum, item) => sum + safeNumber(item.quantity), 0);
  const disabled = saving || checkoutLocked;
  const cardSx = { p: { xs: 1.4, sm: 2 }, borderRadius: 3, border: `1px solid ${ERP_BRAND.border}`, bgcolor: "var(--wp-panel-surface,#fff)" };
  const checkoutButton = (
    <Button ref={checkoutRef} fullWidth variant="contained" size="large"
      disabled={saving || queued > 0 || Boolean(code.trim()) || Boolean(quantityEdit && counterQuantity(quantityEdit.value) === null) || !displayedItems.length || !canConfirm}
      onClick={onCheckout} sx={{ minHeight: 48, bgcolor: ERP_BRAND.orange, textTransform: "none", fontWeight: 950, "&:hover": { bgcolor: "#D9472B" } }}>
      {saving ? "Registrando venta…" : checkoutLocked ? "Verificar y finalizar · F8" : pending > .005 ? "Finalizar con saldo pendiente · F8" : "Cobrar y finalizar · F8"}
    </Button>
  );

  return (
    <Box onKeyDownCapture={handleKeys} data-testid="counter-sale-console">
      <Stack spacing={1.25}>
        {success ? <Alert severity="success">{success}</Alert> : null}
        {error || scanError ? <Alert severity="error">{error || scanError}</Alert> : null}
        <Paper elevation={0} sx={{ ...cardSx, borderTop: `3px solid ${ERP_BRAND.orange}` }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.2 }}>
            <QrCodeScannerRoundedIcon sx={{ color: ERP_BRAND.orange }} />
            <Typography sx={{ fontWeight: 950, flex: 1 }}>Escaneá, cargá la cantidad y seguí</Typography>
            <Chip size="small" color={queued ? "warning" : "success"} variant="outlined" label={queued ? `Buscando ${queued}…` : "Lector listo"} />
          </Stack>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <TextField
              fullWidth autoFocus inputRef={codeRef} label="Escanear próximo ítem"
              placeholder="Código de barras, QR o SKU + Enter" value={code} disabled={disabled}
              autoComplete="off" inputProps={{ spellCheck: false }}
              onChange={(event) => setCode(event.target.value)}
              onKeyDown={(event) => {
                if (event.key !== "Enter" || event.nativeEvent.isComposing) return;
                event.preventDefault();
                event.stopPropagation();
                enqueueScan(code);
              }}
            />
            <Button variant="outlined" startIcon={<SearchRoundedIcon />} disabled={disabled}
              onClick={() => { setShowSearch((current) => !current); window.requestAnimationFrame(() => searchInputRef.current?.focus()); }}
              sx={{ minWidth: 220, whiteSpace: "nowrap", textTransform: "none", fontWeight: 850 }}>
              Buscar por nombre · F2
            </Button>
          </Stack>
          {showSearch ? <Box sx={{ mt: 1.5 }}>{productSearch((product) => {
            if (!product) return;
            setQuantityEdit(null);
            setScanError("");
            setActiveKey(onAddProduct(product));
            setFocusVersion((current) => current + 1);
            setStatus("Ítem agregado. Cambiá la cantidad o escaneá el próximo ítem.");
          })}</Box> : null}
          <Typography variant="caption" role="status" aria-live="polite" sx={{ display: "block", mt: 1, color: ERP_BRAND.muted }}>
            {queued ? "Podés seguir escaneando mientras se agregan los productos." : status}
          </Typography>
        </Paper>

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "minmax(0,1fr)", md: "minmax(0,1fr) 370px" }, gap: 1.5, alignItems: "start" }}>
          <Paper elevation={0} sx={{ ...cardSx, minWidth: 0 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.25 }}>
              <Typography sx={{ fontWeight: 950, fontSize: "1.05rem" }}>Ticket de venta</Typography>
              <Typography variant="body2" color="text.secondary">{displayedItems.length} ítems · {units} unidades</Typography>
            </Stack>
            {!displayedItems.length ? (
              <Box sx={{ py: { xs: 4, sm: 7 }, textAlign: "center", color: ERP_BRAND.muted }}>
                <QrCodeScannerRoundedIcon sx={{ fontSize: 45, mb: 1 }} />
                <Typography sx={{ fontWeight: 850 }}>Escaneá el primer producto</Typography>
                <Typography variant="body2" sx={{ mt: .5 }}>Si repetís el código, se suma una unidad al mismo renglón.</Typography>
              </Box>
            ) : null}
            <Stack spacing={.75}>
              {items.map((item, index) => {
                if (!item.productId) return null;
                const key = counterSaleLineKey(item);
                const product = products.find((candidate) => entityId(candidate) === item.productId);
                const variants = (product?.variants || product?.variations || []).filter((variant) => variant.active !== false);
                const editing = quantityEdit?.key === key;
                return (
                  <Box key={key} data-testid="counter-ticket-line" sx={{ p: 1.2, borderRadius: 2, border: "1px solid", borderColor: activeKey === key ? "rgba(243,90,58,.5)" : ERP_BRAND.border, bgcolor: activeKey === key ? "rgba(243,90,58,.035)" : "transparent" }}>
                    <Stack direction="row" alignItems="start" spacing={1}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 900 }}>{item.name}</Typography>
                        <Typography variant="caption" color="text.secondary">{item.code || "Sin código"}</Typography>
                      </Box>
                      <IconButton size="small" color="error" disabled={disabled} aria-label={`Quitar ${item.name}`}
                        onClick={() => { setQuantityEdit(null); setActiveKey(""); onRemoveItem(index); focusCode(); }}>
                        <DeleteOutlineRoundedIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                    {variants.length > 1 ? (
                      <TextField select size="small" fullWidth label={`Variante de ${item.name}`} value={item.variantId || ""}
                        error={!item.variantId} helperText={!item.variantId ? "Elegí la variante para poder cobrar." : undefined}
                        disabled={disabled} sx={{ mt: .75, mb: 1 }}
                        onChange={(event) => { setQuantityEdit(null); setActiveKey(onSelectVariant(index, event.target.value)); setFocusVersion((current) => current + 1); }}>
                        {variants.map((variant) => <MenuItem key={entityId(variant)} value={entityId(variant)}>
                          {Object.values(variant.attributes || {}).filter(Boolean).join(" / ") || variant.sku || entityId(variant)}
                        </MenuItem>)}
                      </TextField>
                    ) : null}
                    <Box sx={{ display: "grid", gridTemplateColumns: "100px minmax(80px,1fr) minmax(90px,1fr)", gap: 1, alignItems: "center", mt: 1 }}>
                      <TextField size="small" label="Cantidad" value={editing ? quantityEdit.value : item.quantity} disabled={disabled}
                        inputRef={(input) => { if (input) quantityRefs.current.set(key, input); else quantityRefs.current.delete(key); }}
                        inputProps={{ inputMode: "decimal", "data-counter-quantity": key, "aria-label": `Cantidad de ${item.name}` }}
                        onFocus={(event) => { burstRef.current = emptyBurst(); event.target.select(); }}
                        onChange={(event) => setQuantityEdit({ key, value: event.target.value })}
                        onBlur={() => { burstRef.current = emptyBurst(); if (editing && !skipQuantityBlurRef.current) commitQuantity(); }}
                      />
                      <Box sx={{ textAlign: "right" }}>
                        <Typography variant="caption" color="text.secondary">Precio unitario</Typography>
                        <Typography sx={{ fontWeight: 750 }}>{formatMoney(item.unitPrice, currency)}</Typography>
                      </Box>
                      <Box sx={{ textAlign: "right" }}>
                        <Typography variant="caption" color="text.secondary">Subtotal</Typography>
                        <Typography sx={{ fontWeight: 950 }}>{formatMoney(Math.max(0, safeNumber(item.quantity) * safeNumber(item.unitPrice) - safeNumber(item.discountAmount) + safeNumber(item.taxAmount)), currency)}</Typography>
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.4 }}>
              Cantidad seleccionada: escribí el número y Enter. F4 vuelve al lector. F8 cobra la venta.
            </Typography>
          </Paper>

          <Stack spacing={1.25} sx={{ position: { md: "sticky" }, top: 0, minWidth: 0 }}>
            <Paper elevation={0} sx={{ ...cardSx, bgcolor: "#19242F", color: "#fff" }}>
              <Typography variant="overline" sx={{ color: "#FFAA96", fontWeight: 900 }}>TOTAL A COBRAR</Typography>
              <Typography data-testid="counter-total" sx={{ fontSize: { xs: 30, sm: 36 }, fontWeight: 950, lineHeight: 1.15 }}>{formatMoney(total, currency)}</Typography>
              <Stack direction="row" justifyContent="space-between" sx={{ mt: 1.5, gap: 1 }}>
                <Typography variant="body2">Registrado: {formatMoney(paid, currency)}</Typography>
                <Typography variant="body2" sx={{ color: pending > .005 ? "#FFCC80" : "#A5D6A7" }}>Pendiente: {formatMoney(pending, currency)}</Typography>
              </Stack>
            </Paper>
            {paymentPanel}
            {pending > .005 ? <Alert severity="warning" sx={{ py: .25 }}>Quedan {formatMoney(pending, currency)} por cobrar.</Alert> : null}
            {!canConfirm ? <Alert severity="info">Tu rol puede guardar un borrador. Para cobrar y finalizar necesitás permiso de confirmación.</Alert> : null}
            {checkoutTarget ? createPortal(checkoutButton, checkoutTarget) : checkoutButton}
            <Typography variant="caption" color="text.secondary">Al finalizar se registra la venta, el cobro y la salida de stock.</Typography>
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
}
